import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Fuel, Gauge, Eraser, Check, Camera, DollarSign, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

/**
 * ReturnHandoffModal — closes out an `active` rental. Mirror of the pickup
 * modal but captures return mileage + fuel and shows a live estimate of the
 * mileage-overage / fuel-shortage charges that will be pulled from the
 * customer's deposit. Backend authoritative — this is just a preview.
 */
export default function ReturnHandoffModal({ rental, onClose, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mileage, setMileage] = useState("");
  const [fuel, setFuel] = useState(rental.pickup_fuel_level || "F");
  const [notes, setNotes] = useState("");
  const [sig, setSig] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/rentals/${rental.id}/return-photo`, fd);
      setPhotos((p) => [...p, { id: data.file_id, url: data.url }]);
      toast.success("Photo added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ------- Live preview of return charges (client-side, matches backend) -------
  const FUEL = { E: 0, "1/4": 0.25, "1/2": 0.5, "3/4": 0.75, F: 1 };
  const pickupMi = Number(rental.pickup_mileage || 0);
  const days = Number(rental.days || 1);
  const dailyLimit = rental.daily_mileage_limit ?? null;
  const returnMi = Number(mileage) || 0;
  const milesDriven = Math.max(0, returnMi - pickupMi);
  const allowed = dailyLimit ? dailyLimit * days : null;
  const overageMi = allowed ? Math.max(0, milesDriven - allowed) : 0;
  const overageCharge = +(overageMi * 0.45).toFixed(2);
  const pickupFuelFrac = FUEL[rental.pickup_fuel_level] ?? 1;
  const returnFuelFrac = FUEL[fuel] ?? 1;
  const fuelGap = Math.max(0, pickupFuelFrac - returnFuelFrac);
  const gallonsShort = +(fuelGap * 20).toFixed(2);
  const fuelCharge = +(gallonsShort * 9.99).toFixed(2);
  const additional = +(overageCharge + fuelCharge).toFixed(2);
  const deposit = Number(rental.deposit || 0);
  const refund = Math.max(0, +(deposit - additional).toFixed(2));
  const balanceDue = Math.max(0, +(additional - deposit).toFixed(2));

  const submit = async () => {
    if (!mileage || Number(mileage) < 0) return toast.error("Enter the return mileage");
    if (Number(mileage) < pickupMi) return toast.error(`Return mileage can't be below pickup (${pickupMi.toLocaleString()})`);
    if (!sig) return toast.error("Get the customer's return signature");
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/rentals/${rental.id}/return-handoff`, {
        mileage: Number(mileage),
        fuel_level: fuel,
        inspection_notes: notes.trim() || null,
        return_signature_data: sig,
      });
      toast.success(
        r.data.charges_breakdown?.additional_charges > 0
          ? `Return closed · $${r.data.charges_breakdown.additional_charges.toFixed(2)} in charges`
          : "Return closed · no additional charges"
      );
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Return failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto">
      <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); submit(); }}
        data-testid="return-handoff-modal"
        className="w-full max-w-2xl bg-[#0B1120] rounded-3xl border border-cyan/40 shadow-2xl p-5 sm:p-6 space-y-5 my-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-cyan text-[10px]">Return handoff</div>
            <div className="font-serif text-2xl leading-tight">Close out rental</div>
            <div className="text-xs text-white/60 mt-1">
              {rental.vehicle_name} · {rental.full_name} · pickup {pickupMi.toLocaleString()} mi / {rental.pickup_fuel_level}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white text-2xl px-2">×</button>
        </div>

        {/* Photos */}
        <section>
          <div className="label-caps text-cyan text-[10px] mb-2">1 · Return photos</div>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p) => <img key={p.id} src={p.url} alt="" className="aspect-square object-cover rounded-lg border border-cyan/30" />)}
            <label className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition ${uploading ? "border-white/10 text-white/40" : "border-cyan/40 text-cyan hover:bg-cyan/5"}`}>
              <Camera size={20} />
              <span className="text-[10px] label-caps mt-1">{uploading ? "…" : "Add"}</span>
              <input type="file" accept="image/*" capture="environment"
                data-testid="return-photo-input"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                disabled={uploading} className="hidden" />
            </label>
          </div>
        </section>

        {/* Mileage + Fuel */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="label-caps text-cyan text-[10px] mb-1.5 flex items-center gap-1">
              <Gauge size={11} /> 2 · Return odometer
            </div>
            <input type="number" min={pickupMi} inputMode="numeric"
              data-testid="return-mileage"
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              placeholder={`min ${pickupMi.toLocaleString()}`}
              style={{ background: "#000", color: "#FFFFFF" }}
              className="w-full font-mono text-xl font-bold border-2 border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/50 rounded-lg px-4 py-3 text-center" />
          </label>
          <div>
            <div className="label-caps text-cyan text-[10px] mb-1.5 flex items-center gap-1">
              <Fuel size={11} /> 3 · Fuel level
            </div>
            <div className="grid grid-cols-5 gap-1">
              {["E","1/4","1/2","3/4","F"].map((f) => (
                <button key={f} type="button"
                  data-testid={`return-fuel-${f.replace("/","-")}`}
                  onClick={() => setFuel(f)}
                  className={`py-2.5 rounded-lg text-sm font-semibold border transition ${fuel === f ? "bg-cyan text-midnight border-cyan" : "border-white/10 text-white/60 hover:border-cyan/40"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Live charge preview */}
        {mileage && (
          <section data-testid="return-charges-preview"
            className="rounded-2xl border border-cyan/25 bg-cyan/5 p-4 space-y-1.5 text-sm">
            <div className="label-caps text-cyan text-[10px] mb-2 flex items-center gap-1">
              <DollarSign size={11} /> Live charge preview
            </div>
            <Row k={`Miles driven (${milesDriven.toLocaleString()} mi)`}
                 v={allowed ? `${milesDriven.toLocaleString()} / ${allowed.toLocaleString()} allowed` : "unlimited"} />
            {overageCharge > 0 && (
              <Row k={`Mileage overage (${overageMi} mi × $0.45)`} v={`$${overageCharge.toFixed(2)}`} tone="warn" />
            )}
            {fuelCharge > 0 && (
              <Row k={`Fuel shortage (${gallonsShort} gal × $9.99)`} v={`$${fuelCharge.toFixed(2)}`} tone="warn" />
            )}
            <div className="border-t border-cyan/20 pt-2 mt-2">
              <Row k="Additional charges" v={`$${additional.toFixed(2)}`} bold />
              <Row k="Deposit on file" v={`$${deposit.toFixed(2)}`} />
              {additional === 0 ? (
                <Row k="Deposit refund pending" v={`$${deposit.toFixed(2)}`} tone="good" />
              ) : refund > 0 ? (
                <Row k="Deposit refund pending" v={`$${refund.toFixed(2)}`} tone="good" />
              ) : balanceDue > 0 ? (
                <Row k="Balance due from customer" v={`$${balanceDue.toFixed(2)}`} tone="warn" />
              ) : null}
            </div>
            {additional > 0 && (
              <div className="text-[11px] text-amber-300 flex items-start gap-1 pt-1">
                <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                <span>These charges will auto-post to the deposit ledger on submit.</span>
              </div>
            )}
          </section>
        )}

        {/* Notes */}
        <label className="block">
          <div className="label-caps text-cyan text-[10px] mb-1.5">4 · Return notes (optional)</div>
          <textarea rows={2} value={notes}
            data-testid="return-notes"
            onChange={(e) => setNotes(e.target.value)}
            placeholder="New damage, unusual wear, missing items…"
            style={{ background: "#000", color: "#FFFFFF" }}
            className="w-full border-2 border-white/15 focus:border-cyan focus:outline-none rounded-lg px-3 py-2 text-sm" />
        </label>

        {/* Signature */}
        <section>
          <div className="label-caps text-cyan text-[10px] mb-2">5 · Customer signature (return)</div>
          <MiniSig value={sig} onChange={setSig} />
        </section>

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose}
            className="flex-1 py-3 rounded-full text-xs label-caps border border-white/12 text-white/70 hover:border-white/30">
            Cancel
          </button>
          <button type="submit"
            disabled={submitting || !mileage || !sig}
            data-testid="return-handoff-submit"
            className="flex-[2] py-3 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
            <Check size={13} /> {submitting ? "Closing out…" : "Close rental · mark completed"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Row = ({ k, v, tone, bold }) => (
  <div className="flex items-center justify-between text-xs py-0.5">
    <span className={`${bold ? "text-white font-medium" : "text-white/60"}`}>{k}</span>
    <span className={
      tone === "warn" ? "text-amber-300 font-semibold"
      : tone === "good" ? "text-lime-300 font-semibold"
      : bold ? "text-cyan font-semibold" : "text-white/80"
    }>{v}</span>
  </div>
);

function MiniSig({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!value);
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, c.width, c.height);
  }, []);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const p = pos(e); const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#FFF"; ctx.lineWidth = 3.5; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return; drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = useCallback(() => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false); onChange("");
  }, [onChange]);
  return (
    <div className="rounded-xl border-2 border-cyan/50 overflow-hidden relative" style={{ background: "#000" }}>
      <canvas ref={canvasRef} width={720} height={160}
        data-testid="return-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full h-32 touch-none block cursor-crosshair"
        style={{ background: "#000", display: "block" }} />
      {hasInk && (
        <button type="button" onClick={clear}
          className="absolute top-1.5 right-1.5 text-[10px] label-caps text-white/70 bg-black/40 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
          <Eraser size={9} /> Clear
        </button>
      )}
    </div>
  );
}
